import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  TextField,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { auth } from "../../../components/firebase";
import { useWalletAuth } from "../../../components/WalletConnect";
import { useWalletClient } from "wagmi";
import {
  erc20Abi,
  parseUnits,
  maxUint256,
  createPublicClient,
  http,
} from "viem";
import { mainnet, bsc, polygon, arbitrum } from "viem/chains";
import {  ButtonBase } from "@mui/material";
import {
  OX_NATIVE,
  SUPPORTED_CHAINS,
  STABLE_DECIMALS_BY_CHAIN,
  USDC_DECIMALS_BY_CHAIN,
  TOKENS_BY_CHAIN,
  CHAIN_META,
  getTokenIcon, getChainIcon,
} from "./tokens";

const GLASS = {
  bg: "linear-gradient(135deg, rgba(18,22,28,.96) 0%, rgba(10,13,17,.92) 100%)",
  border: "1px solid rgba(255,255,255,.10)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
};


const TOKEN_ICON_SCALE = {
  USDT: 2.2,
  USDC: 2.2,
  USDE: 2.0,
  ETH: 2.0,
  BNB: 2.8,
  MATIC: 2.4,
  POL: 2.4,
  ARB: 2.2,
};

const CHAIN_ICON_SCALE = {
  1: 2.2,
  56: 2.6,
  137: 2.6,
  42161: 2.6,
};


function iconScaleToken(sym) {
  return TOKEN_ICON_SCALE[String(sym || "").toUpperCase()] || 1.25;
}
function iconScaleChain(chainId) {
  return CHAIN_ICON_SCALE[Number(chainId)] || 1.3;
}


function getCfBase() {
  return (process.env.REACT_APP_CF_BASE || "").replace(/\/+$/, "");
}

async function postAuthedJson(path, body) {
  const base = getCfBase();
  if (!base) throw new Error("REACT_APP_CF_BASE is not set");

  const url = `${base}/${path}`;
  const token = await auth.currentUser?.getIdToken?.();
  if (!token) throw new Error("Not authenticated");

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body || {}),
  });

  const text = await res.text();
  if (!res.ok) throw new Error(text || "Request failed");
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// форматирование base units → human (для fee / buyAmount)
function formatBaseUnits(amountStr, decimals, maxFraction = 6) {
  const raw = (amountStr || "0").replace(/^0+/, "") || "0";
  const d = Number(decimals) || 0;

  if (d <= 0) return raw;

  if (raw.length <= d) {
    const padded = raw.padStart(d, "0");
    const whole = "0";
    const frac = padded.slice(0, maxFraction).replace(/0+$/, "");
    return frac ? `${whole}.${frac}` : whole;
  }

  const whole = raw.slice(0, raw.length - d);
  const fracRaw = raw.slice(raw.length - d);
  const frac = fracRaw.slice(0, maxFraction).replace(/0+$/, "");
  return frac ? `${whole}.${frac}` : whole;
}

function fmtUsd(v) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "$0.00";
  return `$${n.toFixed(2)}`;
}

function shortAddr(a) {
  if (!a) return "";
  const s = String(a);
  return s.length > 10 ? `${s.slice(0, 6)}…${s.slice(-4)}` : s;
}

function getNativeSymbol(chainId) {
  if (chainId === 56) return "BNB";
  if (chainId === 137) return "MATIC";
  return "ETH";
}

/* ===== decimals() ABI + cache ===== */
const erc20DecimalsAbi = [
  {
    type: "function",
    name: "decimals",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint8" }],
  },
];

const decimalsCache = new Map(); // key: `${chainId}:${addressLower}` -> number

function isAddressLike(a) {
  const s = String(a || "");
  if (!s) return false;
  if (s === OX_NATIVE) return true;
  return /^0x[a-fA-F0-9]{40}$/.test(s);
}

async function getTokenDecimals(publicClient, token, chainId) {
  if (!token) throw new Error("No token");
  if (token.isNative) return 18;

  // Fast-path: known stables (avoid RPC decimals call)
  if (token.symbol === "USDT") return STABLE_DECIMALS_BY_CHAIN[chainId] ?? 6;
  if (token.symbol === "USDC") return USDC_DECIMALS_BY_CHAIN[chainId] ?? 6;
  if (token.symbol === "USDe") return 18;

  const key = `${chainId}:${String(token.address).toLowerCase()}`;
  if (decimalsCache.has(key)) return decimalsCache.get(key);

  if (!publicClient) throw new Error("No publicClient");

  const d = await publicClient.readContract({
    address: token.address,
    abi: erc20DecimalsAbi,
    functionName: "decimals",
  });

  const num = Number(d);
  if (!Number.isFinite(num) || num <= 0 || num > 36) throw new Error("Bad token decimals");

  decimalsCache.set(key, num);
  return num;
}

/* =========================
   3.1 RPC map (env)
========================= */
const CHAIN_VIEM = {
  1: mainnet,
  56: bsc,
  137: polygon,
  42161: arbitrum,
};

function rpcUrlForChain(chainId) {
  if (chainId === 1)
    return process.env.REACT_APP_RPC_MAINNET || process.env.REACT_APP_RPC_INFURA_FRONT || "";
  if (chainId === 56) return process.env.REACT_APP_RPC_BSC || "";
  if (chainId === 137) return process.env.REACT_APP_RPC_POLYGON || "";
  if (chainId === 42161) return process.env.REACT_APP_RPC_ARBITRUM || "";
  return "";
}

const pcCache = new Map(); // chainId -> publicClient
function makePublicClient(chainId) {
  const cid = Number(chainId);
  if (pcCache.has(cid)) return pcCache.get(cid);

  const chain = CHAIN_VIEM[cid];
  if (!chain) return null;

  const url = rpcUrlForChain(cid);
  const pc = createPublicClient({
    chain,
    transport: url ? http(url) : http(),
  });

  pcCache.set(cid, pc);
  return pc;
}




export default function WalletDepositSwapModal({
  open,
  onClose,
  platformHotWallet = "",
  portfolioBalanceUsd = 0,
  prefetchedAvail = [],
  prefetchedLoading = false,
  prefetchedError = "",
}) {
  const {
    address,
    isConnected,
    chainIdNum,
    isLoadingBalance,
    balances,
    startWalletAuth,
    openNetworks,
  } = useWalletAuth();

  const { data: walletClient } = useWalletClient();

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const [selectedToken, setSelectedToken] = useState(null);
  const [selectedDecimals, setSelectedDecimals] = useState(null);

  const [amountStr, setAmountStr] = useState("");
  const [usdHint, setUsdHint] = useState("");

  const [depositId, setDepositId] = useState("");
  const [expectedStable, setExpectedStable] = useState(0);
  const [priceResp, setPriceResp] = useState(null);
  const [quoteResp, setQuoteResp] = useState(null);

  const [hotWallet, setHotWallet] = useState("");

 

  // wallet chain (0 пока сеть не определилась)
  const chainId = Number(chainIdNum || 0);
  const isSupportedChain = SUPPORTED_CHAINS.includes(chainId);

  // 1.2 tokenList strictly from TOKENS_BY_CHAIN[chainId]
  const tokenList = useMemo(() => {
    if (!chainId) return [];
    return TOKENS_BY_CHAIN[chainId] || [];
  }, [chainId]);

  const stableDecimals = STABLE_DECIMALS_BY_CHAIN[chainId] || 6;
  const nativeSymbol = getNativeSymbol(chainId);

  const balancesSafe = balances || {};

  // direct deposit USDT on BSC (no 0x)
  const isDirectBnbUsdt = chainId === 56 && selectedToken?.symbol === "USDT";

  // hotWallet load on open
  useEffect(() => {
    if (!open) return;

    const fallback = String(platformHotWallet || "").trim();
    if (fallback) setHotWallet(fallback);

    let alive = true;
    (async () => {
      try {
        const base = getCfBase();
        if (!base) throw new Error("No CF base");

        const res = await fetch(`${base}/config`, { method: "GET" });
        if (!res.ok) throw new Error("Config fetch failed");

        const cfg = await res.json();
        if (!alive) return;

        const hw = String(cfg?.platformHotWallet || "").trim();
        if (hw) setHotWallet(hw);
      } catch {
        // ignore
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, platformHotWallet]);

  // reset state when close / open changes
  useEffect(() => {
    if (!open) {
      setStep(1);
      setBusy(false);
      setMsg("");
      setError("");

      setSelectedToken(null);
      setSelectedDecimals(null);

      setAmountStr("");
      setUsdHint("");
      setExpectedStable(0);

      setPriceResp(null);
      setQuoteResp(null);
      setDepositId("");

      
    }
  }, [open]);

  // reset selection if chain changed
  useEffect(() => {
    setSelectedToken(null);
    setSelectedDecimals(null);
    setAmountStr("");
    setUsdHint("");
    setExpectedStable(0);
    setPriceResp(null);
    setQuoteResp(null);
    setDepositId("");
    setStep(1);
    setError("");
    setMsg("");
  }, [chainId]);

  const amountNumber = useMemo(() => {
    const v = parseFloat(String(amountStr).replace(",", "."));
    return Number.isFinite(v) && v > 0 ? v : 0;
  }, [amountStr]);

  const decimalsReady = useMemo(() => {
    if (!selectedToken) return false;
    if (selectedToken.isNative) return true;
    return Number.isFinite(selectedDecimals) && selectedDecimals > 0;
  }, [selectedToken, selectedDecimals]);

  const hotWalletReady = useMemo(() => {
    return isAddressLike(hotWallet) && hotWallet !== OX_NATIVE;
  }, [hotWallet]);

  // walletLabel: native first, else any stable
  const walletLabel = useMemo(() => {
    if (!isConnected || !address) return "Wallet (not connected)";
    if (isLoadingBalance) return "Wallet (Loading…)";

    const native = balancesSafe[nativeSymbol];
    if (native && Number(native.amountNum || 0) > 0) {
      return `Wallet (${shortAddr(address)}), ~${native.amount} ${nativeSymbol}`;
    }

    const anyStable =
      balancesSafe.USDT?.amountNum > 0
        ? { v: balancesSafe.USDT.amountNum }
        : balancesSafe.USDC?.amountNum > 0
        ? { v: balancesSafe.USDC.amountNum }
        : balancesSafe.USDe?.amountNum > 0
        ? { v: balancesSafe.USDe.amountNum }
        : null;

    if (anyStable) {
      return `Wallet (${shortAddr(address)}), ~${fmtUsd(anyStable.v)}`;
    }

    return `Wallet (${shortAddr(address)})`;
  }, [isConnected, address, isLoadingBalance, balancesSafe, nativeSymbol]);

  const feeBreakdown = useMemo(() => {
    const p = priceResp;
    if (!p?.fees?.integratorFee) return null;

    const intFee = p.fees.integratorFee;
    const feeStr = formatBaseUnits(String(intFee.amount || "0"), stableDecimals, 8);
    const buyStr = formatBaseUnits(String(p.buyAmount || "0"), stableDecimals, 8);

    const feeUsd = Number(feeStr) || 0;
    const buyUsd = Number(buyStr) || 0;
    const gross = feeUsd + buyUsd;

    if (!Number.isFinite(feeUsd) || feeUsd <= 0 || gross <= 0) return null;

    const pct = (feeUsd / gross) * 100;
    return { feeUsd, pct };
  }, [priceResp, stableDecimals]);

  // ===== step control =====
  const goBack = () => {
    if (busy) return;
    if (step <= 1) return;

    if (step === 2) {
      setSelectedToken(null);
      setSelectedDecimals(null);
      setAmountStr("");
      setUsdHint("");
      setExpectedStable(0);
      setPriceResp(null);
      setQuoteResp(null);
      setDepositId("");
      setStep(1);
      setError("");
      setMsg("");
      return;
    }

    setStep((prev) => Math.max(1, prev - 1));
    setError("");
    setMsg("");
  };

  const handlePickToken = useCallback(
    async (sym) => {
      const t = tokenList.find((x) => x.symbol === sym);
      setSelectedToken(t || null);
      setSelectedDecimals(null);

      setStep(2);
      setError("");
      setMsg("");
      setUsdHint("");
      setExpectedStable(0);
      setPriceResp(null);
      setQuoteResp(null);
      setDepositId("");

      try {
        if (!t) throw new Error("Token not found");

        if (!t.isNative) {
          // decimals are fetched from CURRENT wallet chain publicClient
          // use viem client from env for the wallet chain to avoid wagmi weirdness
          const pc = makePublicClient(chainId);
          if (!pc) throw new Error("No public client");
          const d = await getTokenDecimals(pc, t, chainId);
          setSelectedDecimals(d);
        } else {
          setSelectedDecimals(18);
        }
      } catch (e) {
        setSelectedDecimals(null);
        setError(e?.message || "Failed to load token decimals");
      }
    },
    [tokenList, chainId]
  );

  const handleAmountChange = (e) => {
    const v = e.target.value;
    if (/^\d*([.,]\d*)?$/.test(v)) {
      setAmountStr(v);

      // direct USDT BNB: hint == amount
      if (isDirectBnbUsdt) {
        const num = parseFloat(v.replace(",", "."));
        if (Number.isFinite(num) && num > 0) {
          setUsdHint(num.toFixed(2));
          setExpectedStable(num);
        } else {
          setUsdHint("");
          setExpectedStable(0);
        }
      }
    }
  };

  const applyPercent = (p) => {
    if (!selectedToken) return;
    const bal = balancesSafe[selectedToken.symbol];
    const base = bal?.amountNum || 0;
    if (!Number.isFinite(base) || base <= 0) return;

    const v = (base * p) / 100;
    setAmountStr(v.toString());

    if (isDirectBnbUsdt && v > 0) {
      setUsdHint(v.toFixed(2));
      setExpectedStable(v);
    }
  };

  const fetchPrice = async () => {
    if (!selectedToken || !walletClient || !address) return;
    if (amountNumber <= 0) return;

    if (!hotWalletReady) {
      setError("Hot wallet is not ready (config not loaded).");
      return;
    }

    if (!isSupportedChain) {
      setError(`Unsupported chain ${chainId}.`);
      return;
    }

    if (isDirectBnbUsdt) return;

    setBusy(true);
    setError("");
    setMsg("");

    try {
      if (!selectedToken) throw new Error("No token selected");
      if (!decimalsReady) throw new Error("Token decimals not loaded");

      const sellUnits = parseUnits(amountNumber.toString(), selectedDecimals || 18);

      const resp = await postAuthedJson("swapPrice", {
        chainId,
        taker: address,
        sellToken: selectedToken.address,
        sellAmount: sellUnits.toString(),
        slippageBps: 100,
      });

      const price =
        resp?.price || resp?.data?.price || resp?.priceResp || resp?.result?.price || resp?.price;

      if (!resp?.ok || !price) {
        throw new Error(typeof resp === "string" ? resp : "swapPrice failed");
      }

      setPriceResp(price);

      const buyHuman = formatBaseUnits(String(price.buyAmount || "0"), stableDecimals);
      const buyNum = parseFloat(buyHuman) || 0;
      setExpectedStable(buyNum);
      setUsdHint(buyNum > 0 ? buyNum.toFixed(2) : "");

      setStep(3);
    } catch (e) {
      setError(e?.message || "Price error");
    } finally {
      setBusy(false);
    }
  };

  const runFlow = async () => {
    if (!selectedToken || !walletClient || !address) return;
    if (amountNumber <= 0) return;

    if (!hotWalletReady) {
      setError("Hot wallet is not ready (config not loaded).");
      return;
    }

    if (!isSupportedChain) {
      setError(`Unsupported chain ${chainId}.`);
      return;
    }

    setBusy(true);
    setError("");
    setMsg("");

    try {
      if (!selectedToken) throw new Error("No token selected");
      if (!decimalsReady) throw new Error("Token decimals not loaded");

      const sellUnits = parseUnits(amountNumber.toString(), selectedDecimals || 18);

      // 1) Direct BSC USDT deposit (no 0x)
      if (isDirectBnbUsdt) {
        setMsg("Sending USDT deposit…");

        const txHash = await walletClient.writeContract({
          address: selectedToken.address,
          abi: erc20Abi,
          functionName: "transfer",
          args: [hotWallet, sellUnits],
          account: address,
        });

        setMsg("Finalizing deposit…");

        const credited = await postAuthedJson("submitDirectDeposit", {
          txHash,
        });

        setMsg(
          credited?.already
            ? "Deposit already credited."
            : `Deposit credited: ${credited?.amount?.toFixed?.(2) ?? credited?.amount} USD`
        );
        setStep(4);
        return;
      }

      // 2) 0x flow
      const price = priceResp;
      if (!price) throw new Error("No price data");

      if (price?.issues?.allowance?.spender && !selectedToken.isNative) {
        const spender = price.issues.allowance.spender;

        setMsg("Approving token…");
        await walletClient.writeContract({
          address: selectedToken.address,
          abi: erc20Abi,
          functionName: "approve",
          args: [spender, maxUint256],
          account: address,
        });
      }

      setMsg("Getting firm quote…");
      const resp = await postAuthedJson("swapQuote", {
        chainId,
        taker: address,
        sellToken: selectedToken.address,
        sellAmount: sellUnits.toString(),
        slippageBps: 100,
      });

      if (!resp?.ok || !resp?.quote || !resp?.depositId) {
        throw new Error(typeof resp === "string" ? resp : "swapQuote failed");
      }

      const depId = resp.depositId;
      const quote = resp.quote;

      setQuoteResp(quote);
      setDepositId(depId || "");

      setMsg("Sending transaction…");

      const tx = quote?.transaction;
      if (!tx || !tx.to || !tx.data) throw new Error("Invalid transaction from 0x");

      const txValue = tx.value ? BigInt(tx.value) : 0n;
      const gasLimit = tx.gas ? BigInt(tx.gas) : undefined;

      const txHash = await walletClient.sendTransaction({
        account: address,
        to: tx.to,
        data: tx.data,
        ...(txValue > 0n ? { value: txValue } : {}),
        ...(gasLimit ? { gas: gasLimit } : {}),
      });

      setMsg("Finalizing deposit…");

      const credited = await postAuthedJson("submitSwapDeposit", {
        chainId,
        depositId: depId,
        txHash,
      });

      setMsg(
        credited?.already
          ? "Deposit already credited."
          : `Deposit credited: ${credited?.amount?.toFixed?.(2) ?? credited?.amount} USD`
      );
      setStep(4);
    } catch (e) {
      setError(e?.message || "Swap deposit error");
    } finally {
      setBusy(false);
    }
  };

  // primary button action
  const onPrimary = async () => {
    if (!isConnected || !address) {
      try {
        await startWalletAuth?.();
      } catch {
        // ignore
      }
      return;
    }

    if (step === 1) return;

    if (step === 2) {
      if (isDirectBnbUsdt) await runFlow();
      else await fetchPrice();
      return;
    }
    if (step === 3) {
      await runFlow();
      return;
    }
    if (step === 4) {
      onClose();
    }
  };

  const primaryLabel = useMemo(() => {
    if (!isConnected || !address) return "Connect wallet";
    if (step === 2 && isDirectBnbUsdt) return "Deposit";
    if (step === 2) return "Continue";
    if (step === 3) return "Confirm Order";
    if (step === 4) return "Done";
    return "Continue";
  }, [step, isDirectBnbUsdt, isConnected, address]);

  const disabledPrimary = useMemo(() => {
    if (busy) return true;

    if (!isConnected || !address) return false;

    if (!hotWalletReady) return true;

    if (step === 1) return true;

    if (step === 2) {
      if (!selectedToken) return true;
      if (amountNumber <= 0) return true;
      if (!decimalsReady) return true;
      return false;
    }

    if (step === 3) {
      if (!decimalsReady) return true;
      if (!priceResp) return true;
      return false;
    }

    return false;
  }, [busy, step, amountNumber, decimalsReady, hotWalletReady, selectedToken, priceResp, isConnected, address]);

  const selectedSymbol = selectedToken?.symbol || "";
  const youSendDisplay = amountStr || "0.00";
  const youReceiveDisplay =
    expectedStable && expectedStable > 0 ? expectedStable.toFixed(2) : amountStr || "0.00";

  const selectedTokenAvailable =
    selectedSymbol && balancesSafe[selectedSymbol] ? balancesSafe[selectedSymbol].amount : null;

  



  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: GLASS.bg,
          border: GLASS.border,
          color: GLASS.text,
          boxShadow: "0 24px 70px rgba(0,0,0,.55)",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          px: 2.2,
          pt: 1.8,
          pb: 1.2,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {step > 1 && (
          <IconButton onClick={goBack} sx={{ mr: 0.5, color: "#cbd5e1" }} disabled={busy}>
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        )}

        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950, fontSize: 18, lineHeight: 1.2 }}>Deposit</Typography>
          <Typography sx={{ color: GLASS.subtext, fontSize: 12, mt: 0.3 }}>
            Portfolio Balance: {fmtUsd(portfolioBalanceUsd)}
          </Typography>
        </Box>

        <IconButton onClick={onClose} sx={{ ml: "auto", color: "#cbd5e1" }} disabled={busy}>
          <CloseIcon />
        </IconButton>
      </Box>

      {busy && <LinearProgress sx={{ height: 1.5 }} />}

      <Divider sx={{ borderColor: "rgba(148,163,184,.16)" }} />

      <DialogContent sx={{ px: 2.2, py: 1.8 }}>
        {/* Source */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 12, color: GLASS.subtext, mb: 0.5 }}>Source</Typography>
          <Typography sx={{ fontSize: 13 }}>{walletLabel}</Typography>
        </Box>

       

      {/* Token picker (Polymarket-style) */}
<Box sx={{ mb: 2 }}>
  <Typography sx={{ fontSize: 12, color: GLASS.subtext, mb: 0.8 }}>
    Deposit token
  </Typography>

  {!isConnected || !address ? (
    <Typography sx={{ fontSize: 11, color: GLASS.subtext }}>
      Connect wallet to load balances.
    </Typography>
  ) : prefetchedLoading ? (
    <Typography sx={{ fontSize: 11, color: GLASS.subtext }}>
      Loading balances…
    </Typography>
  ) : prefetchedError ? (
    <Typography sx={{ fontSize: 11, color: "#f97373" }}>
      {prefetchedError}
    </Typography>
  ) : prefetchedAvail.length === 0 ? (
    <Typography sx={{ fontSize: 11, color: GLASS.subtext }}>
      No balances ≥ $5 found on supported networks.
    </Typography>
  ) : (
    <Box
      sx={{
        border: "1px solid rgba(148,163,184,.20)",
        borderRadius: 2,
        overflow: "hidden",
        background: "rgba(15,23,42,.55)",
      }}
    >
      {prefetchedAvail.map((x, idx) => {
        const cid = Number(x.chainId);
        const sym = String(x.token?.symbol || "").toUpperCase();
        const chainName = CHAIN_META?.[cid]?.name || `Chain ${cid}`;
        const isOnWalletChain = Number(chainId) === cid;

        const tokenIcon = getTokenIcon(sym);
        const chainIcon = getChainIcon(cid);
        const usd = Number(x.usdNum || 0);

        return (
          <ButtonBase
            key={`${cid}:${sym}:${idx}`}
            disabled={step > 1}
            onClick={() => {
              if (!isOnWalletChain) {
                setError(`Switch wallet network to ${chainName} (chain ${cid}) to use ${sym}.`);
                openNetworks?.();
                return;
              }
              setError("");
              handlePickToken(sym);
            }}
            sx={{
              width: "100%",
              textAlign: "left",
              px: 1.4,
              py: 1.1,
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              borderTop: idx === 0 ? "none" : "1px solid rgba(148,163,184,.14)",
              opacity: isOnWalletChain ? 1 : 0.55,
              "&:hover": { background: "rgba(148,163,184,.08)" },
            }}
          >
 {/* Token icon + chain badge (readable) */}
<Box sx={{ position: "relative", width: 48, height: 48, flex: "0 0 auto" }}>
  {/* круг под токен */}
  <Box
    sx={{
      width: 48,
      height: 48,
      borderRadius: 999,
      background: "rgba(255,255,255,.06)",
      border: "1px solid rgba(255,255,255,.10)",
      boxShadow: "inset 0 0 0 1px rgba(0,0,0,.25)",
      display: "grid",
      placeItems: "center",
      overflow: "hidden",
    }}
  >
    <Box
      component="img"
      src={tokenIcon || undefined}
      alt={sym}
      sx={{
  width: "100%",
  height: "100%",
  objectFit: "contain",
  display: "block",
  transform: `scale(${iconScaleToken(sym)})`,
  transformOrigin: "center",
  filter: "drop-shadow(0 2px 10px rgba(0,0,0,.55))",
}}

    />
  </Box>

  {/* круг-бейдж сети */}
  {!!chainIcon && (
    <Box
      sx={{
        position: "absolute",
        right: -6,
        bottom: -6,
        width: 24,
        height: 24,
        borderRadius: 999,
        background: "rgba(2,6,23,.95)",
        border: "1px solid rgba(255,255,255,.22)",
        boxShadow: "0 6px 18px rgba(0,0,0,.55)",
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
      }}
    >
      <Box
        component="img"
        src={chainIcon}
        alt={chainName}
        sx={{
  width: "100%",
  height: "100%",
  objectFit: "contain",
  display: "block",
  transform: `scale(${iconScaleChain(cid)})`,
  transformOrigin: "center",
  filter: "drop-shadow(0 2px 8px rgba(0,0,0,.55))",
}}

      />
    </Box>
  )}
</Box>



            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 900, lineHeight: 1.15 }}>
                {sym}
              </Typography>
              <Typography sx={{ fontSize: 11, color: GLASS.subtext, mt: 0.15 }}>
                {chainName} • {x.amountStr ?? x.amountNum?.toFixed?.(6) ?? "0"}
              </Typography>
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 900 }}>
                ${usd.toFixed(2)}
              </Typography>
              {!isOnWalletChain && (
                <Typography sx={{ fontSize: 10, color: GLASS.subtext, mt: 0.15 }}>
                  Switch network
                </Typography>
              )}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  )}
</Box>


        {/* Step 2: amount */}
        {step >= 2 && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: "1px solid rgba(148,163,184,.24)",
              background: "rgba(15,23,42,.6)",
              mb: 2,
            }}
          >
            <Typography sx={{ fontSize: 12, color: GLASS.subtext, mb: 0.5 }}>
              You send ({selectedToken?.symbol || "TOKEN"})
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                variant="standard"
                value={amountStr}
                onChange={handleAmountChange}
                placeholder="0.00"
                InputProps={{
                  disableUnderline: true,
                  style: { fontSize: 24, color: "#e5e7eb" },
                }}
                sx={{ flex: 1 }}
              />

              <Box sx={{ display: "flex", gap: 0.5 }}>
                {[25, 50, 75, 100].map((p) => (
                  <Button
                    key={p}
                    size="small"
                    onClick={() => applyPercent(p)}
                    sx={{ textTransform: "none", fontSize: 11, minWidth: 0, px: 0.8 }}
                  >
                    {p === 100 ? "Max" : `${p}%`}
                  </Button>
                ))}
              </Box>
            </Box>

            {selectedToken && (
              <Typography sx={{ fontSize: 11, color: GLASS.subtext, mt: 0.4 }}>
                Available: {selectedTokenAvailable ?? "0"} {selectedToken.symbol}
              </Typography>
            )}

            {selectedToken && !decimalsReady && (
              <Typography sx={{ fontSize: 11, color: GLASS.subtext, mt: 0.4 }}>
                Loading token decimals…
              </Typography>
            )}

            {usdHint && (
              <Typography sx={{ fontSize: 12, color: GLASS.subtext, mt: 0.5 }}>
                You receive ≈ {usdHint} USD
              </Typography>
            )}
          </Box>
        )}

        {/* Step 3: confirmation (only swap) */}
        {step >= 3 && !isDirectBnbUsdt && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: "1px solid rgba(148,163,184,.24)",
              background: "rgba(15,23,42,.5)",
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography sx={{ fontSize: 12, color: GLASS.subtext }}>Source</Typography>
              <Typography sx={{ fontSize: 12 }}>{walletLabel}</Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography sx={{ fontSize: 12, color: GLASS.subtext }}>Destination</Typography>
              <Typography sx={{ fontSize: 12 }}>
                Platform Wallet ({shortAddr(hotWallet) || "—"})
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography sx={{ fontSize: 12, color: GLASS.subtext }}>Estimated time</Typography>
              <Typography sx={{ fontSize: 12 }}>{"< 1 min"}</Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", my: 1.2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 11, color: GLASS.subtext }}>You send</Typography>
                <Typography sx={{ fontSize: 13 }}>
                  {youSendDisplay} {selectedSymbol || "TOKEN"}
                </Typography>
              </Box>
              <SwapHorizIcon sx={{ fontSize: 18, mx: 1, color: GLASS.subtext }} />
              <Box sx={{ flex: 1, textAlign: "right" }}>
                <Typography sx={{ fontSize: 11, color: GLASS.subtext }}>You receive</Typography>
                <Typography sx={{ fontSize: 13 }}>{youReceiveDisplay} USD</Typography>
              </Box>
            </Box>

            {feeBreakdown && (
              <Box sx={{ mt: 0.8 }}>
                <Typography sx={{ fontSize: 12, color: GLASS.subtext }}>
                  Transaction breakdown
                </Typography>
                <Typography sx={{ fontSize: 12 }}>
                  {fmtUsd(feeBreakdown.feeUsd)} • {feeBreakdown.pct.toFixed(2)}%
                </Typography>
              </Box>
            )}

            <Typography sx={{ fontSize: 11, color: GLASS.subtext, mt: 1 }}>
              By clicking on Confirm Order, you agree to our terms.
            </Typography>
          </Box>
        )}

        {msg && <Typography sx={{ fontSize: 12, color: GLASS.subtext, mt: 0.5 }}>{msg}</Typography>}
        {error && <Typography sx={{ fontSize: 12, color: "#f97373", mt: 0.5 }}>{error}</Typography>}
      </DialogContent>

      <Box
        sx={{
          px: 2.2,
          pb: 1.8,
          pt: 0,
          display: "flex",
          gap: 1,
          alignItems: "center",
        }}
      >
        <Button onClick={goBack} disabled={busy || step <= 1} sx={{ textTransform: "none", fontSize: 13 }}>
          Back
        </Button>

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          onClick={onPrimary}
          disabled={disabledPrimary}
          sx={{ textTransform: "none", fontWeight: 900, borderRadius: 2 }}
        >
          {busy ? "Processing…" : primaryLabel}
        </Button>
      </Box>
    </Dialog>
  );
}